import { useState, useCallback } from "react";
import { toast } from "react-toastify";

import {
  listBroadcastTemplates,
  createBroadcastTemplate,
  updateBroadcastTemplate,
  deleteBroadcastTemplate,
  getBroadcastSpintaxPreview,
} from "../../../services/messaging.api";
import toastError from "../../../errors/toastError";

const CATEGORIES = [
  { value: "promotional",   label: "Promocional" },
  { value: "informational", label: "Informativo" },
  { value: "retention",     label: "Retención" },
  { value: "transactional", label: "Transaccional" },
];

const EMPTY_FORM = {
  name:     "",
  body:     "",
  category: "promotional",
  isActive: true,
};

export function useBroadcastTemplatesPage() {
  const [templates, setTemplates]       = useState([]);
  const [count, setCount]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [searchParam, setSearchParam]   = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [pageNumber, setPageNumber]     = useState(1);
  const [hasMore, setHasMore]           = useState(false);

  const [modalOpen, setModalOpen]       = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);

  const [spintaxPreview, setSpintaxPreview] = useState(null);

  const fetchTemplates = useCallback(async (page = 1, search = searchParam, category = categoryFilter) => {
    setLoading(true);
    try {
      const { data } = await listBroadcastTemplates({ searchParam: search, category: category || undefined, pageNumber: page, pageSize: 20 });
      if (page === 1) {
        setTemplates(data.records);
      } else {
        setTemplates(prev => [...prev, ...data.records]);
      }
      setCount(data.count);
      setHasMore(data.hasMore);
      setPageNumber(page);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [searchParam, categoryFilter]);

  const openCreateModal = () => {
    setSelectedTemplate(null);
    setForm(EMPTY_FORM);
    setSpintaxPreview(null);
    setModalOpen(true);
  };

  const openEditModal = (template) => {
    setSelectedTemplate(template);
    setForm({
      name:     template.name,
      body:     template.body,
      category: template.category,
      isActive: template.isActive,
    });
    setSpintaxPreview(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedTemplate(null);
    setSpintaxPreview(null);
  };

  const openDeleteModal = (template) => {
    setSelectedTemplate(template);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === "body") setSpintaxPreview(null);
  };

  const handleSpintaxPreview = async () => {
    if (!form.body.trim()) return;
    try {
      const { data } = await getBroadcastSpintaxPreview({ text: form.body });
      setSpintaxPreview(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedTemplate) {
        await updateBroadcastTemplate(selectedTemplate.id, form);
        toast.success("Plantilla actualizada");
      } else {
        await createBroadcastTemplate(form);
        toast.success("Plantilla creada");
      }
      closeModal();
      fetchTemplates(1);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await deleteBroadcastTemplate(selectedTemplate.id);
      toast.success("Plantilla eliminada");
      closeDeleteModal();
      fetchTemplates(1);
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await updateBroadcastTemplate(template.id, {
        isActive: !template.isActive,
      });
      fetchTemplates(pageNumber);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSearch = (value) => {
    setSearchParam(value);
    fetchTemplates(1, value, categoryFilter);
  };

  const handleCategoryFilter = (value) => {
    setCategoryFilter(value);
    fetchTemplates(1, searchParam, value);
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) fetchTemplates(pageNumber + 1);
  };

  return {
    templates, count, loading, searchParam, categoryFilter,
    hasMore, pageNumber, modalOpen, deleteModalOpen,
    selectedTemplate, form, saving, spintaxPreview,
    CATEGORIES,
    fetchTemplates, openCreateModal, openEditModal, closeModal,
    openDeleteModal, closeDeleteModal, handleFormChange,
    handleSpintaxPreview, handleSave, handleDelete,
    handleToggleActive, handleSearch, handleCategoryFilter, handleLoadMore,
  };
}
